import {
  type Game,
  getMachinesSearchTaggingOptions,
  getMachinesSearchTaggingQueryKey,
  postGameByGameIdEndMutation,
} from "@aura-frontend/omnibus-az-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  toast,
} from "@win-reality/react-ui";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import tagGameBg from "@/assets/tag-game-bg.webp";
import { handleErrorNotification } from "@/pages/Tagging/utils/handleErrorNotification";

import { isGameSession } from "../Scorekeeping/hooks/useActiveGame";
import { FieldIcon } from "./icons";
import { ModeCard } from "./ModeCard";

const SESSION_LABELS: Record<string, string> = {
  bullpen: "Bullpen",
  batting_practice: "Batting Practice",
};

/**
 * Session select for Scorekeeping V2, which only scores games. Opens the
 * machine's game, or game setup; a bullpen or batting practice session on
 * the machine has to be ended first.
 */
const Welcome = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: machineId } = useParams<{ id: string }>();
  const [isChecking, setIsChecking] = useState(false);
  const [otherSession, setOtherSession] = useState<Game | null>(null);

  /** The machine's current session, fresh, so the game page agrees with it. */
  const fetchCurrentSession = async () => {
    const { machines } = await queryClient.fetchQuery({
      ...getMachinesSearchTaggingOptions(),
      staleTime: 0,
    });
    return machines.find((m) => m.machineUuid === machineId)?.currentGame;
  };

  const handleGameSelect = async () => {
    setIsChecking(true);
    try {
      const session = await fetchCurrentSession();
      if (!session) {
        navigate("setup");
      } else if (isGameSession(session.type)) {
        navigate("game");
      } else {
        setOtherSession(session);
      }
    } catch (error) {
      handleErrorNotification(
        "Couldn't check the SmartPark's session",
        error as Error,
      );
    } finally {
      setIsChecking(false);
    }
  };

  const sessionLabel = SESSION_LABELS[otherSession?.type ?? ""] ?? "Session";

  const { mutate: endSession, isPending: isEnding } = useMutation({
    ...postGameByGameIdEndMutation(),
    onSuccess: async () => {
      toast(`The ${sessionLabel.toLowerCase()} has been ended`, {
        variant: "success",
      });
      await queryClient.refetchQueries({
        queryKey: getMachinesSearchTaggingQueryKey(),
      });
      setOtherSession(null);
      navigate("setup");
    },
    onError: (error) =>
      handleErrorNotification(
        `Couldn't end the ${sessionLabel.toLowerCase()}`,
        error,
      ),
  });

  const handleEndSession = () => {
    if (!otherSession?.gameUuid) return;
    endSession({ body: {}, path: { game_id: otherSession.gameUuid } });
  };

  return (
    <>
      <div
        className="relative h-full w-full bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${tagGameBg})` }}
      >
        {/* Left panel with mode cards */}
        <div className="absolute left-0 top-0 h-full w-full max-w-xs overflow-y-auto py-6 pl-16">
          <div className="flex flex-col gap-3">
            <ModeCard
              label="Game"
              icon={<FieldIcon className="h-6 w-6" />}
              onClick={handleGameSelect}
              isLoading={isChecking}
              data-testid="welcome-game-mode-button"
            />
          </div>
        </div>
      </div>

      {/* Another session is running on the machine */}
      <Dialog
        open={otherSession !== null}
        onOpenChange={(open) => !open && !isEnding && setOtherSession(null)}
        defaultCancelButton={false}
        size="md"
      >
        {({ showDivider, setScrollContainer }) => (
          <>
            <DialogScrollableContent setScrollContainer={setScrollContainer}>
              <DialogHeader>
                <DialogTitle>{`Active ${sessionLabel}`}</DialogTitle>
              </DialogHeader>

              <p className="text-text-light">
                There is an active {sessionLabel.toLowerCase()} on this
                SmartPark. Starting a game will end it. This action cannot be
                undone.
              </p>
            </DialogScrollableContent>

            <DialogFooter showDivider={showDivider}>
              <div className="flex w-full justify-between">
                <Button
                  variant="ghost"
                  type="button"
                  className="underline"
                  onClick={() => setOtherSession(null)}
                  isDisabled={isEnding}
                  data-testid="welcome-active-session-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary-flat-light"
                  onClick={handleEndSession}
                  isLoading={isEnding}
                  aria-busy={isEnding}
                  data-testid="welcome-active-session-end-button"
                >
                  {`End ${sessionLabel} & Start Game`}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </>
  );
};

export default Welcome;
